import React, { useEffect, useState, useMemo } from "react";
import { observer } from "mobx-react";
import styles from "../styles/entity.module.css";
import emissionViewStore from "../../Store/marco/emissionViewStore.js";
import commonStore from "../../Store/marco/commonStore.js";
//tree-chart
import { TreeNode, Tree } from "react-organizational-chart";
import { getFilterCom, getEditableSubTreeIds } from "../utils/getFilterCom.js";
import {
  buildTreeMap,
  getCompanyNodeClass,
  getAncestorIdsForOperator,
} from "../utils/buildTree.js";
//翻譯
import { useTranslation } from "react-i18next";
import OrgStructureTreeLegend from "../../OrganizationalStructure/components/OrgStructureTreeLegend.js";

import { companiesForYear } from "../../OrganizationalStructure/utils/company-operating-status";

const OrgTree = observer(() => {
  const { orgList, rootId, roleId } = commonStore;
  const { _handleSetAuthComArr } = emissionViewStore;
  const { t } = useTranslation("ActivityMain");

  const orgData = useMemo(() => {
    return orgList
      .filter((item) => item.nodeStatus !== "hidden")
      .map((item) => ({
        ...item,
        treeType:
          item.orgType === "CATEGORY"
            ? item.depth === 0
              ? "ROOT"
              : `Level_${item.depth}_Category`
            : item.orgType === "COMPANY"
              ? item.companyType
              : null,
      }));
  }, [orgList]);
  // console.log("orgData", orgData);

  const handleNodeClick = (e, label, value, logType, nodeStatus) => {
    e.stopPropagation();

    if (nodeStatus === "readonly") {
      return;
    }

    emissionViewStore._handleSetCurrentCompanyName(label);
    emissionViewStore._handleSetCurrentCId(value);

    // 取出對應類別下的公司
    const filterCom = getFilterCom(orgList, value, rootId);

    // 類別底下的公司列表
    _handleSetAuthComArr(filterCom);
    emissionViewStore._handleSetSelectedView("Emission Activity");

    console.log(logType, label);
  };

  const buildTreeMap = (data) => {
    const map = {};
    data.forEach((item) => {
      item.children = []; // 為每個節點初始化空的陣列
      map[item.value] = item; // 建立 ID 到節點的映射表
    });

    const roots = [];

    //建立父子關係
    data.forEach((item) => {
      if (item.parentId && map[item.parentId]) {
        map[item.parentId].children.push(item);
      } else {
        roots.push(item); // 如果沒有父節點，就是根節點
      }
    });

    return roots;
  };

  const treeData = useMemo(() => buildTreeMap(orgData), [orgData]);

  // 根據 orgType 決定節點css
  const getNodeClass = (orgType, treeType, nodeStatus) => {
    const baseClass =
      orgType === "CATEGORY"
        ? styles.nodeCategory
        : orgType === "COMPANY"
          ? getCompanyNodeClass(treeType)
          : styles.node;

    return nodeStatus === "readonly"
      ? `${baseClass} ${styles.readonlyNode}`
      : baseClass;
  };

  const renderTree = (node) => {
    const nodeClass = getNodeClass(
      node.orgType,
      node.treeType,
      node.nodeStatus,
    );

    // 如果是 CATEGORY 節點且有子節點，需要分別處理 CATEGORY 和 COMPANY 子節點
    if (node.orgType === "CATEGORY" && node.children.length > 0) {
      const categoryChildren = node.children.filter(
        (child) => child.orgType === "CATEGORY",
      );
      const companyChildren = node.children.filter(
        (child) => child.orgType === "COMPANY",
      );

      const childNodes = categoryChildren.map((child) => renderTree(child));

      // 如果有 COMPANY 子節點，將它們合併到一個節點中
      if (companyChildren.length > 0) {
        const companyListNode = (
          <TreeNode
            key={`${node.value}-companies`}
            label={
              <div className={styles.companyListContainer}>
                {companyChildren.map((company) => (
                  <div
                    key={company.value}
                    className={`${styles.node} ${getNodeClass(
                      company.orgType,
                      company.treeType,
                      company.nodeStatus,
                    )} ${styles.companyItem}`}
                  >
                    <div
                      className={styles.companyContent}
                      onClick={(e) =>
                        handleNodeClick(
                          e,
                          company.label,
                          company.value,
                          "company",
                          company.nodeStatus,
                        )
                      }
                    >
                      {/* <BusinessIcon /> */}
                      <span className={styles.companyName}>
                        {company.label}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            }
          />
        );
        childNodes.push(companyListNode);
      }

      return (
        <TreeNode
          label={
            <div
              className={`${styles.node} ${nodeClass}`}
              onClick={(e) =>
                handleNodeClick(
                  e,
                  node.label,
                  node.value,
                  "category",
                  node.nodeStatus,
                )
              }
            >
              {node.label}
            </div>
          }
          key={node.value}
        >
          {childNodes}
        </TreeNode>
      );
    }

    // 對於其他類型的節點，保持原來的邏輯
    return (
      <TreeNode
        label={
          <div
            className={`${styles.node} ${nodeClass}`}
            onClick={(e) =>
              handleNodeClick(
                e,
                node.label,
                node.value,
                "其他節點",
                node.nodeStatus,
              )
            }
          >
            {node.label}
          </div>
        }
        key={node.value}
      >
        {node.children.map((child) => renderTree(child))}
      </TreeNode>
    );
  };

  if (!treeData.length) return null;

  return (
    <>
      {treeData.map((root) => {
        const isSuperAdmin = roleId == 2;
        const realName = isSuperAdmin ? t("Super Admin") : root.label;

        return (
          <Tree
            key={root.value}
            label={
              <div
                className={`${styles.superAdminNode} ${
                  root.nodeStatus === "readonly" ? styles.readonlyNode : ""
                }`}
                onClick={(e) =>
                  handleNodeClick(
                    e,
                    realName,
                    root.value,
                    "root",
                    root.nodeStatus,
                  )
                }
              >
                {realName}
              </div>
            }
          >
            {root.children.map((child) => renderTree(child))}
          </Tree>
        );
      })}
    </>
  );
});

const OrgStructureTree = observer(() => {
  const { companyList, roleId } = commonStore;

  // 先過濾掉 hidden 狀態的節點，再進行淺拷貝
  const availableCompanies = companiesForYear(companyList || [], emissionViewStore.selectedYear, commonStore.scopeType);
  const orgData = availableCompanies
    .filter((item) => item.nodeStatus !== "hidden")
    .map((item) => ({ ...item }));

  // 當 roleId === 4 時，計算需要加粗框的祖先節點 id 集合
  const ancestorIds =
    roleId === "4" ? getAncestorIdsForOperator(companyList || []) : new Set();

  // 根據 Type 決定節點css
  const getNodeClass = (type, nodeStatus, nodeId) => {
    const baseClass = getCompanyNodeClass(type); // ✅ 共用
    const ancestorClass = ancestorIds.has(nodeId) ? styles.ancestorNode : "";

    if (nodeStatus === "readonly") {
      const readonlyClass =
        type !== "Parent" ? styles.readonlyNode : styles.notAllowedCursor;

      // ✅ ancestorClass 放最後，確保它蓋過 readonly 的 border
      return `${baseClass} ${readonlyClass} ${ancestorClass}`.trim();
    }

    return `${baseClass} ${ancestorClass}`.trim();
  };

  const handleNodeClick = (e, label, value, logType, nodeStatus) => {
    e.stopPropagation();

    // 如果狀態是 readonly，直接阻擋後續所有動作
    if (nodeStatus === "readonly") {
      return;
    }

    emissionViewStore._handleSetCurrentCompanyName(label);
    emissionViewStore._handleSetCurrentCId(value);

    // 取出對應類別下的公司
    const filterComIds = getEditableSubTreeIds(value, availableCompanies);
    console.log("filterComIds", filterComIds);

    // 類別底下的公司列表
    emissionViewStore._handleSetAuthComArr(filterComIds);
    emissionViewStore._handleSetSelectedView("Emission Activity");

    console.log(logType, label);
  };

  const renderTree = (node) => {
    // if (node.nodeStatus === "hidden") return null;
    const nodeClass = getNodeClass(node.type, node.nodeStatus, node.id);

    // 對於其他類型的節點，保持原來的邏輯
    return (
      <TreeNode
        label={
          <div
            className={`${styles.node} ${nodeClass}`}
            onClick={(e) =>
              handleNodeClick(
                e,
                node.companyName,
                node.id,
                "節點",
                node.nodeStatus,
              )
            }
          >
            {node.companyName}
          </div>
        }
        key={node.id}
      >
        {node.children.map((child) => renderTree(child))}
      </TreeNode>
    );
  };

  const treeData = buildTreeMap(orgData);
  if (!treeData.length) return null;

  return (
    <div className={styles.entityContainer}>
      {treeData.map((root) => {
        const rootNodeClass = getNodeClass(root.type, root.nodeStatus);
        return (
          <Tree
            key={root.id}
            label={
              <div
                className={`${styles.node} ${rootNodeClass}`}
                onClick={(e) =>
                  handleNodeClick(
                    e,
                    root.companyName,
                    root.id,
                    "root",
                    root.nodeStatus,
                  )
                }
              >
                {root.companyName}
              </div>
            }
            lineWidth="2px"
            lineColor="#000"
          >
            {root.children.map((child) => renderTree(child))}
          </Tree>
        );
      })}
    </div>
  );
});

export const Entity = observer(() => {
  const { scopeType } = commonStore;

  return (
    <div className={styles.entityContainer}>
      {scopeType === "AUTH" ? <OrgTree /> : <OrgStructureTree />}
    </div>
  );
});
